import { P2GBox, P2GGrid, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import CTAButton from '~/components/core/button/cta-button';
import ModuleFactory from '../modules/module-factory';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import Checkout from './layouts/checkout';
import useQuoteRequirements from '~/hooks/use-quote-requirements';
import { GenerateWorkflow } from '../lib/workflow/generate-workflow';
import { validateForm } from '~/lib/validation';
import useBasket from '~/hooks/use-basket';
import useQuote from '~/hooks/use-quote';
import { Workflow, WorkflowAction, WorkflowScreen } from '~/lib/types/workflow-types';
import BackButton from '~/components/core/button/back-button';
import { BASKETITEMID } from '~/lib/getCurrentItemId';
import { ToStepperTitle } from '~/components/core/stepper/to-stepper-title';
import useOptInsPersister from '~/hooks/use-opt-ins-persister';
import useSenderTypePersister from '~/components/parcel-details/sender-type/use-sender-type-persister';
import { BasketQuote, QuoteAddress, QuoteError, QuoteExtras } from '~/lib/types/basket-types';
import { Form } from '~/lib/constants';
import useWorkflowActions from '~/hooks/use-workflow-actions';
import Loading from '~/components/core/progress/loading';
import Sidebar from '~/components/core/sidebar/checkout-sidebar';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import ServiceUnavailable from '~/components/core/switcher/service-unavailable';
import PriceChangeDialog from '~/components/price-change/price-change-dialog';
import { shouldShowPriceChangeMessage } from '~/lib/quotes';
import useBasketSection from '~/hooks/use-basket-section';
import { BasketSections } from '~/lib/types/enums';
import { quoteToAddToBasketEvent } from '~/lib/analytics';
import useSenderSavedAddressPersister from '~/hooks/use-sender-saved-address-persister';

const FindWorkflowScreen = (workflow: Workflow, path: string): WorkflowScreen | undefined =>
  workflow.screens.find((x) => x.path === path) || workflow.screens[0];

type Status =
  | 'SAVING_CHANGES'
  | 'IDLE'
  | 'API_VALIDATION_ERROR'
  | 'FORM_VALIDATION_ERROR'
  | 'CREATING_ORDER'
  | 'CREATED_ORDER'
  | 'ORDER_ERROR';

export function Component() {
  const { completedActions, clearCompletedActions } = useWorkflowActions();
  const params = useParams();
  const { quote, quoteErrors, locations } = useQuote();
  const { persist: saveOptIns } = useOptInsPersister();
  const { persist: saveSenderType } = useSenderTypePersister();
  const { split, complete, saveChanges, currentItemId, currentAction, loading, quoting } = useBasket();
  const isXs = useP2GMediaQueryOnly('xs');
  const { requiresCustoms, loading: requirementsLoading } = useQuoteRequirements();
  const workflow = useMemo(
    () => GenerateWorkflow(isXs, requiresCustoms, quote, currentAction),
    [isXs, requiresCustoms, quote, currentAction]
  );
  const screen = useMemo(() => FindWorkflowScreen(workflow, params['screen'] || ''), [workflow, params]);
  const [saving, setSaving] = useState<Status>('IDLE');
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [errors, setErrors] = useState({} as Record<string, string>);
  const [inlineQuoteErrors, setInlineQuoteErrors] = useState<QuoteError[]>([]);
  const [prices, setPrices] = useState<{ original: BasketQuote; new: BasketQuote }>();
  const extras = useBasketSection<QuoteExtras>({ name: BasketSections.EXTRAS });
  const origin = useBasketSection<QuoteAddress>({ name: BasketSections.ORIGIN });
  const destination = useBasketSection<QuoteAddress>({ name: BasketSections.DESTINATION });
  const { persistSenderAddress } = useSenderSavedAddressPersister();

  const navigate = useNavigate();
  const { track } = useAnalytics();

  const pageLoaded = useCallback(() => {
    if (screen) {
      track({
        type: 'workflow_screen_view',
        payload: {
          page: `checkout/${screen.path}`,
          workflow: workflow.name,
          pageNumber: (screen.index || 0) + 1,
          pageCount: workflow.screens.length,
          pageNames: workflow.screens.map((x) => `checkout/${x.path}`),
          origin: locations.origin,
          destination: locations.destination
        }
      });
      document.title = ToStepperTitle(screen.path);
    }
  }, [track, screen, workflow, locations]);

  useEffect(() => {
    if (screen?.path && !requirementsLoading) {
      pageLoaded();
    }
  }, [screen?.path, requirementsLoading]);

  useEffect(() => {
    if (!loading && !currentItemId) {
      navigate({ pathname: '../confirm' }, { relative: 'path', replace: true });
    }
  }, [loading, currentItemId, navigate]);

  useEffect(() => {
    setInlineQuoteErrors(quoteErrors);
  }, [quoteErrors]);

  const onBack = useCallback(() => {
    const previousScreen = workflow?.screens[(screen?.index || 0) - 1];
    if (previousScreen?.path) {
      navigate(
        {
          pathname: `../${previousScreen?.path}`,
          search: createSearchParams({ [BASKETITEMID]: currentItemId }).toString()
        },
        { relative: 'path', replace: true }
      );
    }
  }, [workflow, screen, navigate, currentItemId]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      setSaving('SAVING_CHANGES');
      e.preventDefault();
      setValidationAttempt((x) => x + 1);

      const form = e.target as HTMLFormElement;
      const formErrors = validateForm(form);
      setErrors(formErrors);

      if (form.checkValidity() && Object.keys(formErrors).length == 0) {
        // This gets a quote and errors.
        const newQuote = await saveChanges(true);
        if (!newQuote || (newQuote?.errors && newQuote.errors.length > 0)) {
          setInlineQuoteErrors(newQuote?.errors || []);
          setSaving('IDLE');
          track({
            type: 'quote_error',
            payload: { step: screen?.path.toLowerCase(), quoteErrors: newQuote?.errors || [] }
          });
          return;
        }

        // Show Price Change Message.
        if (newQuote && shouldShowPriceChangeMessage(newQuote, quote)) {
          setPrices({ original: quote, new: newQuote });
          setSaving('IDLE');
          track({
            type: 'quote_price_change',
            payload: { step: screen?.path.toLowerCase(), quote: newQuote }
          });
          return;
        }

        // These are the actions to perform on submit and with 0 errors.
        if (screen?.onSubmitActions) {
          // Keep a record of the actions that have actually been completed.
          const confirmedCompletedActions: WorkflowAction[] = [];

          // Communication Preferences
          if (screen.onSubmitActions.includes('COMMS') && completedActions.includes('COMMS')) {
            saveOptIns();
            confirmedCompletedActions.push('COMMS');
          }

          // Sender Type
          if (screen.onSubmitActions.includes('SENDER-TYPE') && completedActions.includes('SENDER-TYPE')) {
            saveSenderType();
            confirmedCompletedActions.push('SENDER-TYPE');
          }

          // Sender Addrress
          if (screen.onSubmitActions.includes('SAVE-SENDER-ADDRESS')) {
            persistSenderAddress();
            confirmedCompletedActions.push('SAVE-SENDER-ADDRESS');
          }

          if (confirmedCompletedActions.length > 0) {
            clearCompletedActions(confirmedCompletedActions);
          }

          // Basket Page
          if (screen.onSubmitActions.includes('BASKET')) {
            await split();
            await complete();

            try {
              track({
                type: 'add_to_cart',
                payload: quoteToAddToBasketEvent(origin.data, destination.data, quote, extras.data)
              });
            } catch (e) {
              console.error("Couldn't track add to cart event", e);
            }

            navigate(
              {
                pathname: `../confirm`,
                search: createSearchParams({}).toString()
              },
              { relative: 'path' }
            );
            return;
          }
        }

        // If none of the actions navigate, and there is a next screen, navigate to that.
        if (screen?.next) {
          navigate(
            {
              pathname: `../${screen?.next}`,
              search: createSearchParams({ [BASKETITEMID]: currentItemId }).toString()
            },
            { relative: 'path' }
          );
          setSaving('IDLE');
          return;
        }
      } else {
        const formErrors = validateForm(form);
        track({
          type: 'checkout_validation_error',
          payload: { step: screen?.path.toLowerCase(), formErrors: formErrors, quoteErrors: quoteErrors }
        });
        setErrors(formErrors);
        setSaving('FORM_VALIDATION_ERROR');
        return;
      }

      setSaving('IDLE');
    },
    [
      quoteErrors,
      track,
      screen,
      saveChanges,
      completedActions,
      clearCompletedActions,
      saveOptIns,
      saveSenderType,
      complete,
      navigate,
      setPrices,
      setValidationAttempt,
      currentItemId,
      quote,
      origin,
      destination,
      extras,
      persistSenderAddress
    ]
  );

  if (!workflow || !screen || !currentItemId) {
    return null;
  }

  return (
    <Checkout workflow={workflow} path={params['screen'] || ''}>
      <form onSubmit={onSubmit} noValidate id={Form.CHECKOUTID}>
        <P2GGrid container spacing={isXs ? 3 : 4} direction="column">
          {screen.modules.map((x, i) => (
            <ModuleFactory
              key={`module-${x}-${i}`}
              errors={errors}
              moduleKey={x}
              wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
            />
          ))}
          {isXs && (
            <P2GGrid item>
              <Suspense fallback={<Loading />}>
                <Sidebar />
              </Suspense>
            </P2GGrid>
          )}
          <P2GGrid item>
            <P2GGrid container spacing={2}>
              <P2GGrid item xs={6}>
                {screen.index !== undefined && screen.index > 0 && (
                  <BackButton onClick={onBack} loading={saving === 'SAVING_CHANGES' || loading} fullWidth={isXs} />
                )}
              </P2GGrid>
              <P2GGrid item xs={6}>
                <P2GBox style={{ display: 'flex', justifyContent: 'end' }}>
                  <CTAButton
                    loading={saving === 'SAVING_CHANGES' || loading || quoting || requirementsLoading}
                    fullWidth={isXs}
                  />
                </P2GBox>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        </P2GGrid>
        {!quoting && !loading && (quoteErrors.length > 0 || inlineQuoteErrors.length > 0) && (
          <ServiceUnavailable
            errors={quoteErrors.length > 0 ? quoteErrors : inlineQuoteErrors}
            key={`VALIDATION-${validationAttempt}`}
          />
        )}
        {prices && (
          <PriceChangeDialog
            key={`PRICE-VALIDATION-${validationAttempt}`}
            originalQuote={prices.original}
            quote={prices.new}
            onAccept={() => {
              setPrices(undefined);
            }}
          />
        )}
      </form>
    </Checkout>
  );
}

Component.displayName = 'New Basket Item Page';
